import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, brandMilestones, brandValues, contactInfo } from '@/mocks/site';
import { organizationSchema } from '@/mocks/schema';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const formData = new FormData(form);
      const resp = await fetch('https://readdy.ai/api/form/d9pmj6hn2trhb35gvhvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后重试' : (serverMsg || '提交失败，请稍后重试'));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const aboutJsonLd = [
    organizationSchema,
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "关于91网",
      "description": `了解${siteInfo.name}的品牌故事、发展历程与服务理念。`,
      "url": `${siteInfo.url}/about`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": siteInfo.url },
        { "@type": "ListItem", "position": 2, "name": "关于我们" },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们"
        description={`了解${siteInfo.name}的品牌故事、发展历程与服务理念。${siteInfo.description}`}
        keywords="91网,关于91网,91网品牌,91网联系方式"
        canonicalUrl={`${siteInfo.url}/about`}
        jsonLd={aboutJsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="mx-auto px-6" style={{ maxWidth: 1280 }}>
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="mt-8 mb-16">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              关于{siteInfo.name}
            </h1>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {siteInfo.name}创立于{siteInfo.foundingYear}年，是国内领先的综合性资源整合与服务平台。我们致力于为用户提供一站式的资源获取体验，汇聚了涵盖设计素材、开发工具、学习教程、行业资讯等多领域的优质资源。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  经过多年的深耕发展，91网已积累了超过500万注册用户和30万+精选资源。我们始终秉持"让资源获取更简单"的初心，通过技术创新和服务升级，不断优化用户体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  未来，91网将继续深化AI技术与资源服务的融合，构建更加智能、高效、全面的资源生态系统，为用户创造更大的价值。
                </p>
              </div>
              <div className="relative h-72 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimal%20office%20interior%20with%20warm%20lighting%20collaborative%20workspace%20with%20clean%20design%20natural%20elements%20professional%20yet%20inviting%20atmosphere%20wood%20accents&width=700&height=400&seq=about-brand-91&orientation=landscape"
                  alt={`${siteInfo.name}品牌故事`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-8 text-center">品牌价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {brandValues.map((v) => (
                <div key={v.title} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`${v.icon} text-xl`} />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-8 text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />
              <div className="space-y-8">
                {brandMilestones.map((m, idx) => (
                  <div key={m.year} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0 md:px-10`}>
                      <span className="text-xs text-primary-600 font-medium">{m.year}</span>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1">{m.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{m.description}</p>
                    </div>
                    <div className="absolute md:relative left-0 md:left-auto md:flex-shrink-0 flex items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center z-10" />
                    </div>
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-6">联系我们</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-phone-line" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">客服电话</p>
                    <p className="text-sm text-foreground-500">{contactInfo.phone}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{contactInfo.workHours}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-500">{contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">公司地址</p>
                    <p className="text-sm text-foreground-500">{contactInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-6">在线留言</h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="true">
                <div>
                  <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="about-message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  />
                </div>
                <div className="honeypot-field" aria-hidden="true">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}